import { useEffect, useMemo, useRef } from 'react';
import {
  FieldExtensionComponentProps,
  useTemplateSecrets,
} from '@backstage/plugin-scaffolder-react';
import { Flex, TextField } from '@backstage/ui';
import { PasswordGeneratorProps } from './types';
import { generatePassword, DEFAULT_PASSWORD_LENGTH } from './utils';

export const PasswordGenerator = ({
  onChange,
  uiSchema,
  schema,
  required,
  rawErrors,
  name,
}: FieldExtensionComponentProps<
  string | undefined,
  PasswordGeneratorProps
>) => {
  const { setSecrets } = useTemplateSecrets();

  const options = useMemo(() => {
    const uiOptions = uiSchema?.['ui:options'] || {};
    return {
      length: uiOptions.length ?? DEFAULT_PASSWORD_LENGTH,
      charset: uiOptions.charset,
      uppercase: uiOptions.uppercase ?? true,
      lowercase: uiOptions.lowercase ?? true,
      numbers: uiOptions.numbers ?? true,
      symbols: uiOptions.symbols ?? false,
      hidden: uiOptions.hidden ?? false,
    };
  }, [uiSchema]);

  const password = useMemo(() => generatePassword(options), [options]);
  const lastEmittedRef = useRef<string>();

  useEffect(() => {
    if (!password || !name) {
      return;
    }
    if (lastEmittedRef.current === password) {
      return;
    }

    lastEmittedRef.current = password;
    const maskedValue = '*'.repeat(password.length);
    onChange(maskedValue);
    setSecrets({ [name]: password });
  }, [password, onChange, setSecrets, name]);

  if (options.hidden) {
    return null;
  }

  const hasError = !!rawErrors?.length;
  const displayValue = password ? '*'.repeat(password.length) : '';
  const fieldTitle = schema.title || 'Generated Password';
  const helperText =
    schema.description || 'Password has been generated and stored securely';

  return (
    <Flex direction="column">
      <TextField
        id="password-generator"
        label={fieldTitle}
        value={displayValue}
        size="medium"
        isRequired={required}
        isReadOnly
        description={hasError ? rawErrors?.join(', ') : helperText}
      />
    </Flex>
  );
};
